import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ContactSalesModal from '../../components/modals/ContactSalesModal';
import { billingAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

export default function PlansPage() {
  const { tenant, updateTenant } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showContactSales, setShowContactSales] = useState(false);

  useEffect(() => {
    // Check for Stripe redirect params
    if (searchParams.get('success') === 'true') {
      setSuccess('Subscription activated successfully! Your plan has been updated.');
      // Clear the URL params
      setSearchParams({});
    } else if (searchParams.get('canceled') === 'true') {
      setError('Checkout was canceled. No charges were made.');
      setSearchParams({});
    }
    loadData();

    // Reset activating state when page is restored from bfcache
    // (user clicked back from Stripe checkout without completing purchase)
    const handlePageShow = (event) => {
      if (event.persisted) {
        setActivating(null);
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        billingAPI.getPlans(),
        billingAPI.getSubscription(),
      ]);
      setPlans(plansRes.data.data?.plans || plansRes.data || []);
      setCurrentSubscription(subRes.data.data?.subscription || subRes.data.subscription);
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (planId) => {
    setActivating(planId);
    setSuccess(null);
    setError(null);

    try {
      // Create Stripe checkout session
      const response = await billingAPI.createCheckout({
        planId,
        billingInterval: 'monthly'
      });

      // Redirect to Stripe checkout
      if (response.data.data?.url) {
        window.location.href = response.data.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Failed to create checkout:', err);
      const message = err.response?.data?.error || 'Failed to start checkout. Please try again.';
      setError(message);
      setActivating(null);
    }
  };

  const currentPlanSlug = currentSubscription?.plan?.slug || tenant?.plan?.slug;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading plans..." />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Plans & Billing</h1>
        <p className="mt-1 text-slate-500 dark:text-slate-400">
          Manage your subscription and billing details.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
          <p className="text-green-700 dark:text-green-300">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Current Plan */}
      {currentSubscription && (
        <Card>
          <Card.Header>
            <Card.Title>Current Subscription</Card.Title>
          </Card.Header>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Plan</p>
              <p className="font-semibold text-slate-900 dark:text-white">
                {currentSubscription.plan?.name || 'Free'}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Status</p>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                currentSubscription.status === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
              }`}>
                {currentSubscription.status}
              </span>
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Expires</p>
              <p className="font-semibold text-slate-900 dark:text-white">
                {currentSubscription.expiresAt
                  ? new Date(currentSubscription.expiresAt).toLocaleDateString()
                  : 'Never'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((plan) => {
          const isCurrent = currentPlanSlug === plan.slug;
          const features = plan.features ? Object.entries(plan.features) : [];

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col ${
                isCurrent ? 'ring-2 ring-primary-500' : ''
              }`}
            >
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center rounded-full bg-primary-600 px-3 py-1 text-xs font-medium text-white">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="p-6 flex-1">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{plan.name}</h3>

                <div className="mt-4">
                  {plan.priceMonthly ? (
                    <>
                      <span className="text-3xl font-bold text-slate-900 dark:text-white">
                        £{plan.priceMonthly}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400">/month</span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-slate-900 dark:text-white">Custom</span>
                  )}
                </div>

                <ul className="mt-6 space-y-3">
                  {plan.maxUsers && (
                    <li className="flex items-start gap-2">
                      <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {plan.maxUsers === 9999 ? 'Unlimited' : `Up to ${plan.maxUsers}`} users
                      </span>
                    </li>
                  )}
                  {features.slice(0, 5).map(([key, value]) => {
                    if (typeof value === 'boolean' && !value) return null;
                    return (
                      <li key={key} className="flex items-start gap-2">
                        <CheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="p-6 pt-0">
                {isCurrent ? (
                  <Button variant="secondary" className="w-full" disabled>
                    Current Plan
                  </Button>
                ) : plan.priceMonthly ? (
                  <Button
                    className="w-full"
                    onClick={() => handleActivate(plan.id)}
                    loading={activating === plan.id}
                  >
                    {currentPlanSlug ? 'Switch Plan' : 'Activate'}
                  </Button>
                ) : (
                  <Button variant="outline" className="w-full" onClick={() => setShowContactSales(true)}>
                    Contact Sales
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Stripe Test Mode Notice */}
      <Card>
        <div className="flex items-center gap-3 text-blue-600 dark:text-blue-400">
          <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <p className="text-sm">
            <span className="font-medium">Stripe Test Mode:</span> Use test card <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">4242 4242 4242 4242</code> with any future expiry and CVC.
          </p>
        </div>
      </Card>

      {/* Contact Sales Modal */}
      <ContactSalesModal
        isOpen={showContactSales}
        onClose={() => setShowContactSales(false)}
        onSuccess={(message) => toast.success(message)}
      />
    </div>
  );
}
